// src/services/notificationService.js
//
// Firestore structure
// ───────────────────
// notifications/{recipientUid}/userNotifications/{autoId}
//   type          : 'subscribe' | 'like' | 'comment' | 'save'
//                   | 'new_listing' | 'status' | 'want' | 'status_like'
//                   | 'want_like' | 'message' | 'system'
//   senderId      : uid of the user who triggered the action ('' for system)
//   targetId      : listingId / statusId / wantId / conversationId (optional per type)
//   listingTitle  : display title for listing-related notifications
//   thumbnailUrl  : cover image of the listing/status/want (optional)
//   listingImageUrl: first photo URL fallback
//   commentText   : first ~80 chars of the comment (comment only)
//   wantDetails   : first ~80 chars of the want's details (want only)
//   messageText   : first ~80 chars of the message (message only)
//   conversationId: for message notifications
//   title         : announcement title (system only)
//   message       : full announcement body, shown untruncated (system only)
//   isRead        : false (set to true by the screen on open)
//   timestamp     : serverTimestamp()

import { auth, db } from '../firebase';
import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  onSnapshot,
  getDocs,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
} from 'firebase/firestore';

// Firestore batches cap at 500 mutations; broadcast in chunks under that.
const BROADCAST_CHUNK_SIZE = 450;

class NotificationService {
  constructor() {
    this._db = db;
    this._auth = auth;
  }

  static getInstance() {
    if (!NotificationService._instance) {
      NotificationService._instance = new NotificationService();
    }
    return NotificationService._instance;
  }

  get _currentUid() {
    return this._auth.currentUser?.uid || null;
  }

  // ── Internal writer (single recipient) ────────────────────────────────────

  async _write(recipientUid, data) {
    // Never notify yourself
    if (recipientUid === this._currentUid) return;

    try {
      const notificationsRef = collection(
        this._db,
        'notifications',
        recipientUid,
        'userNotifications'
      );

      await addDoc(notificationsRef, {
        ...data,
        senderId: this._currentUid || '',
        isRead: false,
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      // Notifications are best-effort; never throw
      console.error('Notification write error:', error);
    }
  }

  // ── Internal writer (fan-out to many recipients, chunked) ────────────────

  async _writeToMany(recipientUids, dataBuilder) {
    const senderId = this._currentUid || '';
    const uids = (recipientUids || []).filter(
      (uid) => uid && uid.length > 0 && uid !== senderId
    );
    if (uids.length === 0) return;

    for (let i = 0; i < uids.length; i += BROADCAST_CHUNK_SIZE) {
      const chunk = uids.slice(i, i + BROADCAST_CHUNK_SIZE);
      const batch = writeBatch(this._db);

      for (const uid of chunk) {
        const ref = doc(
          collection(this._db, 'notifications', uid, 'userNotifications')
        );
        batch.set(ref, {
          ...dataBuilder(),
          senderId,
          isRead: false,
          timestamp: serverTimestamp(),
        });
      }

      try {
        await batch.commit();
      } catch (error) {
        // Best-effort — skip this chunk on failure, keep going with the rest.
        console.error('_writeToMany chunk error:', error);
      }
    }
  }

  // ── Subscribe ─────────────────────────────────────────────────────────────

  async onSubscribe(targetUid) {
    await this._write(targetUid, { type: 'subscribe' });
  }

  // ── Like ──────────────────────────────────────────────────────────────────

  async onLike({
    listingOwnerId,
    listingId,
    listingTitle,
    thumbnailUrl,
    listingImageUrl,
  }) {
    const data = {
      type: 'like',
      targetId: listingId,
      listingTitle: listingTitle,
    };

    if (thumbnailUrl && thumbnailUrl.length > 0) {
      data.thumbnailUrl = thumbnailUrl;
    }
    if (listingImageUrl && listingImageUrl.length > 0) {
      data.listingImageUrl = listingImageUrl;
    }

    await this._write(listingOwnerId, data);
  }

  // ── Comment ───────────────────────────────────────────────────────────────

  async onComment({
    listingOwnerId,
    listingId,
    listingTitle,
    commentText,
    thumbnailUrl,
    listingImageUrl,
  }) {
    const truncatedText =
      commentText.length > 80 ? commentText.substring(0, 80) + '…' : commentText;

    const data = {
      type: 'comment',
      targetId: listingId,
      listingTitle: listingTitle,
      commentText: truncatedText,
    };

    if (thumbnailUrl && thumbnailUrl.length > 0) {
      data.thumbnailUrl = thumbnailUrl;
    }
    if (listingImageUrl && listingImageUrl.length > 0) {
      data.listingImageUrl = listingImageUrl;
    }

    await this._write(listingOwnerId, data);
  }

  // ── Save ──────────────────────────────────────────────────────────────────

  async onSave({
    listingOwnerId,
    listingId,
    listingTitle,
    thumbnailUrl,
    listingImageUrl,
  }) {
    const data = {
      type: 'save',
      targetId: listingId,
      listingTitle: listingTitle,
    };

    if (thumbnailUrl && thumbnailUrl.length > 0) {
      data.thumbnailUrl = thumbnailUrl;
    }
    if (listingImageUrl && listingImageUrl.length > 0) {
      data.listingImageUrl = listingImageUrl;
    }

    await this._write(listingOwnerId, data);
  }

  // ── Status like ───────────────────────────────────────────────────────────
  // Call when the current user likes someone else's status post — mirrors
  // onLike's shape but for the status/want like surface, which has no
  // listing title. targetId is the statusId so a tap-to-navigate handler
  // that wants the exact post can use it, though NotificationItem opens the
  // recipient's own active status deck (they're the owner, not the sender)
  // rather than trying to deep-link a single expiring status doc.

  async onStatusLike({ ownerUid, statusId, thumbnailUrl }) {
    const data = {
      type: 'status_like',
      targetId: statusId,
    };

    if (thumbnailUrl && thumbnailUrl.length > 0) {
      data.thumbnailUrl = thumbnailUrl;
    }

    await this._write(ownerUid, data);
  }

  // ── Want like ─────────────────────────────────────────────────────────────
  // Call when the current user likes someone else's want post. Same shape
  // and same "open the recipient's own active deck" navigation reasoning
  // as onStatusLike above.

  async onWantLike({ ownerUid, wantId, thumbnailUrl }) {
    const data = {
      type: 'want_like',
      targetId: wantId,
    };

    if (thumbnailUrl && thumbnailUrl.length > 0) {
      data.thumbnailUrl = thumbnailUrl;
    }

    await this._write(ownerUid, data);
  }

  // ── New listing ───────────────────────────────────────────────────────────
  // Call from AddPropertyPage after the Firestore doc is saved.
  // Sends a notification to every subscriber of the poster.
  //
  // subscriberUids = the poster's 'subscribers' array from Firestore.

  async onNewListing({
    subscriberUids,
    listingId,
    listingTitle,
    thumbnailUrl,
    listingImageUrl,
  }) {
    await this._writeToMany(subscriberUids, () => {
      const data = {
        type: 'new_listing',
        targetId: listingId,
        listingTitle: listingTitle,
      };
      if (thumbnailUrl && thumbnailUrl.length > 0) {
        data.thumbnailUrl = thumbnailUrl;
      }
      if (listingImageUrl && listingImageUrl.length > 0) {
        data.listingImageUrl = listingImageUrl;
      }
      return data;
    });
  }

  // ── Status ────────────────────────────────────────────────────────────────
  // Call from AddStatusScreen right after the status doc is saved.
  // Unlike new_listing this deliberately only reaches people SUBSCRIBED TO
  // the poster — not every subscriber-of-a-subscriber, not the whole
  // platform — matching "status should notify only subscribed to accounts".
  //
  // subscriberUids = the poster's 'subscribers' array from Firestore
  // (same field onNewListing reads — the set of people who subscribed to
  // this user).
  //
  // targetId is the statusId so a tap-to-navigate handler that wants the
  // exact post can use it, though the viewer screens (StatusViewerScreen)
  // are opened keyed by senderId (the poster), not targetId, since they
  // always show that user's whole active story deck.

  async onStatusPosted({ subscriberUids, statusId, thumbnailUrl }) {
    await this._writeToMany(subscriberUids, () => {
      const data = {
        type: 'status',
        targetId: statusId,
      };
      if (thumbnailUrl && thumbnailUrl.length > 0) {
        data.thumbnailUrl = thumbnailUrl;
      }
      return data;
    });
  }

  // ── Want ──────────────────────────────────────────────────────────────────
  // Call from AddWantScreen right after the want doc is saved.
  // "Wants should notify all sellers on the platform" — a seller is defined
  // as anyone who has ever posted a listing/property, so this fetches the
  // distinct set of uids across the 'listings' collection itself rather
  // than relying on a separate role flag.
  //
  // NOTE: this reads every listing doc to collect distinct owners, which
  // is fine at moderate scale but will get costlier as the listings
  // collection grows. If that becomes a bottleneck, maintain a dedicated
  // `sellers` collection (one doc per uid, written the first time a user
  // posts a listing) and swap _fetchAllSellerUids() to read that instead —
  // nothing else in this method needs to change.
  //
  // Returns the number of recipients the notification was sent to.

  async onWantPosted({ wantId, details, thumbnailUrl }) {
    const sellerUids = await this._fetchAllSellerUids();
    if (sellerUids.length === 0) return 0;

    const trimmedDetails =
      details && details.length > 80 ? details.substring(0, 80) + '…' : details;

    await this._writeToMany(sellerUids, () => {
      const data = {
        type: 'want',
        targetId: wantId,
      };
      if (trimmedDetails && trimmedDetails.length > 0) {
        data.wantDetails = trimmedDetails;
      }
      if (thumbnailUrl && thumbnailUrl.length > 0) {
        data.thumbnailUrl = thumbnailUrl;
      }
      return data;
    });

    return sellerUids.length;
  }

  // Distinct uids of everyone who has ever posted a listing — checks the
  // same two fields listing_page's ownerUid getter does ('uid', falling
  // back to 'userRef'), so it matches however individual listing docs
  // happen to be shaped.
  async _fetchAllSellerUids() {
    const snap = await getDocs(collection(this._db, 'listings'));
    const uids = new Set();
    for (const d of snap.docs) {
      const data = d.data() || {};
      const uid = data.uid?.toString() || data.userRef?.toString();
      if (uid && uid.length > 0) uids.add(uid);
    }
    return Array.from(uids);
  }

  // ── Message ───────────────────────────────────────────────────────────────

  async onMessage({ recipientUid, conversationId, messageText }) {
    const truncatedText =
      messageText.length > 80 ? messageText.substring(0, 80) + '…' : messageText;

    await this._write(recipientUid, {
      type: 'message',
      conversationId: conversationId,
      messageText: truncatedText,
    });
  }

  // ── System broadcast ─────────────────────────────────────────────────────
  // Admin only. Writes a 'system' notification into EVERY user's
  // userNotifications subcollection.
  //
  // Unlike the helpers above this deliberately does NOT go through
  // _write(): system broadcasts go to everyone (including the sending
  // admin) and carry no per-recipient sender info. Firestore batches cap
  // at 500 mutations, so uids are chunked.
  //
  // NOTE: this only writes the Firestore notification docs, matching how
  // every other notification type here works — actual FCM/web-push
  // delivery is expected to be driven by the existing Cloud Function
  // trigger on notification-doc creation, the same one that presumably
  // fans out pushes for likes/comments/messages today. If that trigger
  // doesn't exist yet, this will show up in-app (Activity screen) but
  // won't push.
  //
  // Returns the number of users the broadcast was sent to.

  async broadcastSystemMessage({ title, message }) {
    const usersSnap = await getDocs(collection(this._db, 'users'));
    const uids = usersSnap.docs.map((d) => d.id);
    if (uids.length === 0) return 0;

    for (let i = 0; i < uids.length; i += BROADCAST_CHUNK_SIZE) {
      const chunk = uids.slice(i, i + BROADCAST_CHUNK_SIZE);
      const batch = writeBatch(this._db);

      for (const uid of chunk) {
        const ref = doc(
          collection(this._db, 'notifications', uid, 'userNotifications')
        );
        batch.set(ref, {
          type: 'system',
          title,
          message,
          senderId: '',
          isRead: false,
          timestamp: serverTimestamp(),
        });
      }

      await batch.commit();
    }

    return uids.length;
  }

  // ── Unread count (stream) ─────────────────────────────────────────────────

  unreadCountStream(callback) {
    const uid = this._currentUid;
    if (!uid) {
      callback(0);
      return () => {};
    }

    const q = query(
      collection(this._db, 'notifications', uid, 'userNotifications'),
      where('isRead', '==', false)
    );

    return onSnapshot(
      q,
      (snapshot) => {
        callback(snapshot.size);
      },
      (error) => {
        console.error('unreadCountStream error:', error);
        callback(0);
      }
    );
  }

  // ── Mark all as read ─────────────────────────────────────────────────────

  async markAllAsRead(uid) {
    if (!uid) uid = this._currentUid;
    if (!uid) return;

    try {
      const q = query(
        collection(this._db, 'notifications', uid, 'userNotifications'),
        where('isRead', '==', false)
      );

      const snapshot = await getDocs(q);
      const batch = writeBatch(this._db);

      for (const doc of snapshot.docs) {
        batch.update(doc.ref, { isRead: true });
      }

      await batch.commit();
    } catch (error) {
      console.error('markAllAsRead error:', error);
    }
  }

  // ── Mark single as read ──────────────────────────────────────────────────

  async markAsRead(uid, notificationId) {
    if (!uid) uid = this._currentUid;
    if (!uid || !notificationId) return;

    try {
      const ref = doc(
        this._db,
        'notifications',
        uid,
        'userNotifications',
        notificationId
      );
      await updateDoc(ref, { isRead: true });
    } catch (error) {
      console.error('markAsRead error:', error);
    }
  }

  // ── Delete notification ──────────────────────────────────────────────────

  async deleteNotification(uid, notificationId) {
    if (!uid) uid = this._currentUid;
    if (!uid || !notificationId) return;

    try {
      const ref = doc(
        this._db,
        'notifications',
        uid,
        'userNotifications',
        notificationId
      );
      await deleteDoc(ref);
    } catch (error) {
      console.error('deleteNotification error:', error);
    }
  }

  // ── Clear all notifications ─────────────────────────────────────────────

  async clearAllNotifications(uid) {
    if (!uid) uid = this._currentUid;
    if (!uid) return;

    try {
      const snapshot = await getDocs(
        collection(this._db, 'notifications', uid, 'userNotifications')
      );

      const batch = writeBatch(this._db);
      for (const doc of snapshot.docs) {
        batch.delete(doc.ref);
      }
      await batch.commit();
    } catch (error) {
      console.error('clearAllNotifications error:', error);
    }
  }

  // ── Get notifications (paginated) ──────────────────────────────────────

  async getNotifications(uid, limitCount = 50, lastDoc = null) {
    if (!uid) uid = this._currentUid;
    if (!uid) return { notifications: [], lastDoc: null };

    try {
      let q = query(
        collection(this._db, 'notifications', uid, 'userNotifications'),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      const notifications = [];
      let lastVisible = null;

      snapshot.forEach((doc) => {
        notifications.push({ id: doc.id, ...doc.data() });
        lastVisible = doc;
      });

      return { notifications, lastDoc: lastVisible };
    } catch (error) {
      console.error('getNotifications error:', error);
      return { notifications: [], lastDoc: null };
    }
  }

  // ── Dispose ─────────────────────────────────────────────────────────────

  dispose() {
    // Clean up any subscriptions if needed
  }
}

// ── Singleton export ─────────────────────────────────────────────────────────

const notificationService = NotificationService.getInstance();
export { notificationService };
export default notificationService;